import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import Button from '@mui/material/Button';

import { Container, Loading, ErrorMessage } from '@/src/ui';
import { apiRequest } from '@/src/services';
import { useAuth } from '@/src/app/context/useAuth';
import { type StandardsReportFilterState } from '../_shared/models/standardsReportFilterDTOs';
import StandardsReportFilters from '../_shared/components/StandardsReportFilters';
import type { StudentStandardsSummaryDTO } from './models/studentStandardsReportDTOs';
import StudentStandardsReportTable, {
    type OpenStudentMasteryPayload,
} from './components/StudentStandardsReportTable';
import StudentMasteryModal from './components/StudentMasteryModal';

const StudentStandardsReportPage: React.FC = () => {
    document.title = 'Student Standards Report';

    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [hasSearched, setHasSearched] = useState(false);

    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const UNIVERSAL_FILTER_KEY = `universal-filters-${userId}`;

    const [examNames, setExamNames] = useState<string[]>(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed?.examNames)) return parsed.examNames;
                if (parsed?.examName) return [parsed.examName];
            }
        } catch {
            // intentionally empty
        }
        return [];
    });
    const pendingExamNamesRef = useRef<string[]>([]);

    const [filterState, setFilterState] = useState<StandardsReportFilterState>(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                const parsedExamIds: number[] = Array.isArray(parsed.examIds)
                    ? parsed.examIds.map(Number).filter((n: number) => n > 0)
                    : parsed.examId
                      ? [Number(parsed.examId)]
                      : [];
                return {
                    districtId: parsed.districtId || 0,
                    schoolId: parsed.schoolId || 0,
                    gradeLevelMasterId: parsed.gradeLevelMasterId || 0,
                    classId: parsed.classId || 0,
                    examTypeMasterId: parsed.examTypeMasterId || 0,
                    examIds: parsedExamIds,
                    standardsIds: Array.isArray(parsed.standardsIds)
                        ? parsed.standardsIds.map(Number).filter((n: number) => n > 0)
                        : [],
                    studentId: 0,
                };
            }
        } catch {
            // intentionally empty
        }
        return {
            districtId: 0,
            schoolId: 0,
            gradeLevelMasterId: 0,
            classId: 0,
            examTypeMasterId: 0,
            examIds: [],
            standardsIds: [],
            studentId: 0,
        };
    });

    useEffect(() => {
        if (!userId) return;
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            const existingFilters = saved ? JSON.parse(saved) : {};

            const universalFilters = {
                ...existingFilters,
                gradeLevelMasterId: filterState.gradeLevelMasterId,
                districtId: filterState.districtId,
                schoolId: filterState.schoolId,
                classId: filterState.classId,
                examTypeMasterId: filterState.examTypeMasterId,
                examIds: filterState.examIds,
                standardsIds: filterState.standardsIds,
                examNames,
            };
            localStorage.setItem(UNIVERSAL_FILTER_KEY, JSON.stringify(universalFilters));
        } catch {
            // intentionally empty
        }
    }, [filterState, examNames, userId, UNIVERSAL_FILTER_KEY]);

    const [rows, setRows] = useState<StudentStandardsSummaryDTO[]>([]);
    const [masteryTarget, setMasteryTarget] = useState<OpenStudentMasteryPayload | null>(null);

    const refreshTableData = useCallback((state: StandardsReportFilterState) => {
        setError('');
        setLoaded(false);
        setHasSearched(true);

        const params = new URLSearchParams({
            districtId: String(state.districtId),
            schoolId: String(state.schoolId),
            gradeLevelMasterId: String(state.gradeLevelMasterId),
            classId: String(state.classId),
            examTypeMasterId: String(state.examTypeMasterId || 0),
            studentId: String(Number(state.studentId) || 0),
        });
        for (const id of state.examIds ?? []) params.append('examIds', String(id));
        for (const id of state.standardsIds ?? []) params.append('standardIds', String(id));

        apiRequest<StudentStandardsSummaryDTO[]>(
            'GET',
            `reports/classStandardsReport/student?${params.toString()}`
        )
            .then((res) => {
                setRows(Array.isArray(res.data) ? res.data : []);
                if (pendingExamNamesRef.current.length) setExamNames(pendingExamNamesRef.current);
            })
            .catch(() => {
                setError('Failed to load Student Standards Report');
                setRows([]);
            })
            .finally(() => setLoaded(true));
    }, []);

    const emptyMessage = useMemo(
        () => (hasSearched ? 'No student data found.' : ''),
        [hasSearched]
    );

    useEffect(() => {
        const fs = filterState;
        if (fs.districtId && fs.gradeLevelMasterId && fs.classId && (fs.examIds?.length ?? 0) > 0) {
            refreshTableData(fs);
        } else {
            setLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isLoading = hasSearched && !loaded;

    const singleExamId = filterState.examIds?.length === 1 ? filterState.examIds[0] : null;
    const headerLabel = examNames.length > 0 ? examNames.join(', ') : '';

    return (
        <Container>
            <h1 className="page-header">Student Standards Report</h1>
            <br />

            <StandardsReportFilters
                filterState={filterState}
                setFilterState={setFilterState}
                refreshTableData={refreshTableData}
                onExamNamesChange={(names) => {
                    pendingExamNamesRef.current = names;
                }}
            />

            {headerLabel && (
                <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '8px' }}>
                    {headerLabel}
                </h2>
            )}

            {hasSearched && (
                <>
                    <Loading visible={isLoading} message="Loading Student Standards Report..." />

                    <ErrorMessage error={error} />

                    <Box sx={{ pb: 2, display: 'flex', justifyContent: 'start' }}>
                        {singleExamId !== null && (
                            <div className="ml-auto">
                                <Button
                                    onClick={() => null}
                                    sx={{ textTransform: 'none' }}
                                    variant="outlined"
                                    href={`/reports/questionList/${singleExamId}`}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    View Assessment
                                </Button>
                            </div>
                        )}
                    </Box>

                    {!isLoading && !error && (
                        <StudentStandardsReportTable
                            data={rows}
                            emptyMessage={emptyMessage}
                            onOpenMastery={(payload) => setMasteryTarget(payload)}
                        />
                    )}
                </>
            )}

            {masteryTarget && (
                <StudentMasteryModal
                    open
                    onClose={() => setMasteryTarget(null)}
                    studentId={masteryTarget.studentId}
                    studentName={masteryTarget.studentName}
                    standards={masteryTarget.standards}
                />
            )}
        </Container>
    );
};

export default StudentStandardsReportPage;
